/**
 * AI Credits — read-only balance + purchase flow for the org's
 * monthly AI credit allowance.
 *
 * Visibility model (mirrors backend ``_require_credits_view`` plus
 * ``MANAGE_BILLING`` for purchase):
 *
 *   * MANAGER / EMPLOYEE   — section is not rendered. The parent
 *     billing page renders nothing for these roles.
 *   * HR_ADMIN             — sees balance, progress bar, recent
 *     usage history. Purchase buttons are absent.
 *   * SUPER_ADMIN          — sees everything + can click "Buy" on
 *     any configured credit pack (kicks off a Stripe Checkout).
 *
 * Data sources:
 *   * ``GET /billing/credits``               → balance snapshot
 *   * ``GET /billing/credits/transactions``  → last 10 ledger rows
 *   * ``GET /billing/credits/packs``         → pack catalog +
 *     per-pack ``available`` flag (false when the operator hasn't
 *     configured a Stripe price ID for that pack).
 *
 * Purchase flow: ``POST /billing/credits/checkout`` returns a
 * Stripe Checkout URL; we redirect the tab so the user lands in
 * Stripe's hosted page. After they pay, Stripe fires
 * ``checkout.session.completed`` to our webhook which calls
 * ``credits_service.record_purchase`` to add the credits.
 */
import { useCallback, useEffect, useState } from "react";
import { CreditCard, Sparkles, ShoppingCart, TrendingDown } from "lucide-react";

import {
  getCreditBalance,
  getCreditPacks,
  getCreditTransactions,
  startCreditCheckout,
  type CreditBalance,
  type CreditPack,
  type CreditTransaction,
} from "@/api/billing";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";


/** Color-grade a usage percentage so the progress bar telegraphs
 *  pressure without needing to read the number. 0-60% reads
 *  accent (calm), 60-85% warning (amber), 85-100% danger (red). */
function usageTone(pct: number): {
  bar: string;
  text: string;
  label: string;
} {
  if (pct >= 85) return { bar: "bg-danger", text: "text-danger", label: "Critical" };
  if (pct >= 60) return { bar: "bg-warning", text: "text-warning", label: "Heavy" };
  return { bar: "bg-accent", text: "text-accent", label: "Healthy" };
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

/** Pretty-print a ledger row. Different shapes per transaction
 *  type — ``usage`` shows the command snippet, ``purchase`` the
 *  amount, ``monthly_reset`` the reset banner. */
function TxnRow({ txn }: { txn: CreditTransaction }) {
  const isPositive = txn.amount > 0;
  const amountLabel = isPositive
    ? `+${formatNumber(txn.amount)}`
    : formatNumber(txn.amount);
  const amountClass =
    txn.amount === 0
      ? "text-text-muted"
      : isPositive
        ? "text-success"
        : "text-text-primary";
  return (
    <li className="flex items-start justify-between gap-3 py-2 border-b border-border/40 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-text-primary truncate">
          {txn.description || txn.type}
        </div>
        {txn.command_text && (
          <div className="text-xs text-text-muted truncate font-mono">
            “{txn.command_text}”
          </div>
        )}
        <div className="text-[11px] text-text-muted">
          {formatDate(txn.created_at)}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={cn("text-sm font-semibold tabular-nums", amountClass)}>
          {amountLabel}
        </div>
        <div className="text-[11px] text-text-muted tabular-nums">
          {formatNumber(txn.balance_after)} left
        </div>
      </div>
    </li>
  );
}


export function AiCreditsSection() {
  const { user } = useAuth();
  const { addToast } = useToast();
  // Role gate — keep this layer of defense even though the parent
  // page wraps us in a role check. Defaults to "EMPLOYEE" so we
  // never surface anything to an unrecognized role.
  const role = user?.role ?? "EMPLOYEE";
  const canView = role === "SUPER_ADMIN" || role === "HR_ADMIN";
  const canPurchase = role === "SUPER_ADMIN";

  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [txns, setTxns] = useState<CreditTransaction[] | null>(null);
  const [packs, setPacks] = useState<CreditPack[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [buyingKey, setBuyingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    // Fire all three in parallel; show whatever lands. A single
    // failure doesn't blank the section.
    const [b, t, p] = await Promise.allSettled([
      getCreditBalance(),
      getCreditTransactions(10),
      canPurchase ? getCreditPacks() : Promise.resolve([] as CreditPack[]),
    ]);
    if (b.status === "fulfilled") setBalance(b.value);
    if (t.status === "fulfilled") setTxns(t.value);
    if (p.status === "fulfilled") setPacks(p.value);
    setLoading(false);
  }, [canView, canPurchase]);

  useEffect(() => {
    load();
  }, [load]);

  // Buy a pack → start Stripe Checkout → window.location.assign(url)
  // so the user lands in Stripe's hosted page. We don't open a new
  // tab; Stripe's flow expects a same-tab redirect for the success
  // URL to land back on this page cleanly.
  const onBuy = async (pack: CreditPack) => {
    if (!canPurchase) return;
    setBuyingKey(pack.key);
    try {
      const res = await startCreditCheckout(pack.key);
      if (!res.checkout_url) {
        throw new Error("No checkout URL returned by Stripe");
      }
      window.location.assign(res.checkout_url);
    } catch (err) {
      console.error("credit checkout failed", err);
      addToast(
        "error",
        "Couldn't start checkout. Try again, or contact support.",
      );
      setBuyingKey(null);
    }
  };

  // Hide the section entirely for roles that shouldn't see credit
  // info. Defense-in-depth — the parent page also wraps us.
  if (!canView) return null;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Command Bar Credits</CardTitle>
        </CardHeader>
        <div className="space-y-3 p-4">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </Card>
    );
  }

  if (!balance) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Command Bar Credits</CardTitle>
        </CardHeader>
        <div className="p-4 text-sm text-text-muted">
          Couldn't load your credit balance. Reload to retry.
        </div>
      </Card>
    );
  }

  // Internal orgs have an effectively-unlimited balance; render a
  // single-line "Unlimited" affordance instead of the progress bar +
  // purchase grid (everything is moot for them).
  if (balance.is_internal) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI Command Bar Credits</CardTitle>
        </CardHeader>
        <div className="p-4 flex items-center justify-between gap-4 text-sm">
          <span className="text-text-primary">Internal workspace</span>
          <Badge variant="success">Unlimited</Badge>
        </div>
      </Card>
    );
  }

  // ── Progress math ────────────────────────────────────────────
  const monthlyAllowance = Math.max(0, balance.monthly_allowance);
  const monthlyUsed = Math.max(0, balance.monthly_used);
  const monthlyPctUsed =
    monthlyAllowance === 0
      ? 100
      : Math.min(100, Math.round((monthlyUsed / monthlyAllowance) * 100));
  const tone = usageTone(monthlyPctUsed);

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Command Bar Credits</CardTitle>
      </CardHeader>
      <div className="space-y-6 p-4">
        {/* ── Framing copy ─────────────────────────────────────
            The whole platform works without credits — onboarding,
            reports, enrollment, audits, everything. Credits power
            the command-bar shortcut so admins can skip clicks.
            Lead with this so the rest of the panel doesn't read
            like a paywall. */}
        <p className="text-xs text-text-muted leading-relaxed">
          Every module works from the full interface — no credits
          needed. Credits power the command bar shortcut so you can
          skip the clicks.
        </p>

        {/* ── Headline numbers ────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Monthly remaining
            </div>
            <div className="text-2xl font-bold tabular-nums text-text-primary">
              {formatNumber(balance.monthly_remaining)}
              <span className="text-sm font-normal text-text-muted">
                {" "}
                / {formatNumber(monthlyAllowance)}
              </span>
            </div>
            <div className="text-xs text-text-muted">
              Resets {formatDate(balance.reset_date)}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Purchased balance
            </div>
            <div className="text-2xl font-bold tabular-nums text-text-primary">
              {formatNumber(balance.purchased_balance)}
            </div>
            <div className="text-xs text-text-muted">Never expires</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Total available
            </div>
            <div className="text-2xl font-bold tabular-nums text-accent">
              {formatNumber(balance.total_available)}
            </div>
            <div className={cn("text-xs font-medium", tone.text)}>
              {tone.label}
            </div>
          </div>
        </div>

        {/* ── Progress bar ────────────────────────────────────── */}
        <div>
          <div className="flex justify-between text-xs text-text-muted mb-1.5">
            <span>This month's allowance</span>
            <span className="tabular-nums">
              {monthlyPctUsed}% used
            </span>
          </div>
          <div className="h-2 rounded-full bg-surface-muted overflow-hidden">
            <div
              className={cn("h-full transition-all", tone.bar)}
              style={{ width: `${monthlyPctUsed}%` }}
            />
          </div>
        </div>

        {/* ── Credit-pack catalog (SUPER_ADMIN only) ─────────── */}
        {canPurchase && packs && packs.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Buy more credits
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {packs.map((pack) => (
                <div
                  key={pack.key}
                  className={cn(
                    "relative rounded-xl border p-3 flex flex-col gap-2",
                    pack.popular
                      ? "border-accent/60 bg-accent/5"
                      : "border-border",
                  )}
                >
                  {pack.popular && (
                    <span className="absolute -top-2 right-2 rounded-full bg-accent text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                      Popular
                    </span>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-accent" />
                    <span className="text-sm font-semibold text-text-primary">
                      {formatNumber(pack.credits)}
                    </span>
                  </div>
                  <div className="text-xs text-text-muted">credits</div>
                  <div className="text-lg font-bold text-text-primary tabular-nums">
                    ${pack.price_usd.toFixed(0)}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onBuy(pack)}
                    disabled={!pack.available || buyingKey !== null}
                    isLoading={buyingKey === pack.key}
                  >
                    {pack.available ? "Buy" : "Unavailable"}
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-xs text-text-muted mt-2">
              Purchased credits never expire. Monthly credits reset on the
              first of each month.
            </div>
          </div>
        )}
        {!canPurchase && (
          <div className="rounded-lg border border-border/50 bg-surface-muted/30 px-3 py-2 text-xs text-text-muted flex items-center gap-2">
            <CreditCard className="h-3.5 w-3.5" />
            Ask a SUPER_ADMIN to buy more credits if you're running low.
          </div>
        )}

        {/* ── Recent usage history ────────────────────────────── */}
        {txns && txns.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              Recent activity
            </div>
            <ul className="rounded-lg border border-border/40">
              {txns.map((t) => (
                <li key={t.id} className="px-3">
                  <TxnRow txn={t} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
